import { useEffect, useState } from 'react';
import type { IPractitioner } from '../../../interfaces/practitioner';
import type { GeneralSettingsProps } from './generalSettingsProps';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { getSelectedLanguage } from '../../../../translate';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { SelectSupervisor } from '../../../elements/SelectSupervisor';
import { GeneralSettingsReadOnlyFormGroup } from './GeneralSettingsReadOnlyFormGroup';

export function GeneralSettingsSupervisor(props: Readonly<GeneralSettingsProps>) {
    const [selectedSupervisor, setSelectedSupervisor] = useState<string | null>(null);
    const [selectedSupervisorLabel, setSelectedSupervisorLabel] = useState<string | null>(null);

    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();

    useEffect(() => {
        if (props.employment.identifierDeclarationPractitionerId) {
            initializeSupervisorOption(props.employment.identifierDeclarationPractitionerId);
        } else {
            setSelectedSupervisor(null);
            setSelectedSupervisorLabel(null);
        }
    }, [props.employment.identifierDeclarationPractitionerId, props.isEditing]);

    if (!props.employment) {
        return null;
    }

    if (!props.isEditing) {
        return (
            <GeneralSettingsReadOnlyFormGroup
                label={translate.supervisors.supervisor}
                value={selectedSupervisorLabel}
            />
        );
    }

    return (
        <SelectSupervisor
            errors={props.errors}
            onChangeSupervisor={async (value, label) => {
                setSelectedSupervisor(value);
                setSelectedSupervisorLabel(label ?? null);
                props.onChange('identifierDeclarationPractitionerId', value);
                props.onValidateOne('identifierDeclarationPractitionerId', value ?? null);
            }}
            organizationId={props.employment.organizationId}
            practitionerId={props.employment.practitionerId}
            selectedSupervisor={selectedSupervisor}
            selectedSupervisorLabel={selectedSupervisorLabel}
        />
    );

    async function initializeSupervisorOption(identifierDeclarationPractitionerId: string) {
        const url = `/api/practitioners/${identifierDeclarationPractitionerId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const selectedPractitioner = result.json as IPractitioner;

        if (selectedPractitioner?.id) {
            const label = createLabel(selectedPractitioner);
            setSelectedSupervisorLabel(label);
            setSelectedSupervisor(selectedPractitioner.id);
        }
    }

    function createLabel(practitioner: IPractitioner) {
        return `${getDisplayName(practitioner)} (${translate.identifierDeclarationOrRegistration.identifierDeclaration}: ${
            practitioner.identifierDeclaration
        })`;
    }
}
